/**
 * ExportSessionsModal - Modal for exporting production sessions to Excel
 * Allows users to select a date range and exports all sessions within that range.
 */

import React, { useState, useMemo } from 'react';
import { X, Download, Calendar, Loader2, FileSpreadsheet } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import DatePickerDropdown from '../../../components/DatePickerDropdown';
import { exportSessionsToExcel } from '../../../utils/excelExport';

interface ProductionSession {
  session_number?: string;
  scheduled_date?: string;
  product_sku?: string;
  product_name?: string;
  product_chinese_name?: string;
  packing?: string;
  total_boxes?: number;
  target_output_weight?: number;
  raw_material_item_number?: string;
  raw_material_name?: string;
  raw_material_chinese_name?: string;
  raw_material_cases?: number;
  initial_material_weight?: number;
  brand?: string;
  material_location_code?: string;
  status?: string;
}

interface ExportSessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessions: ProductionSession[];
  defaultStartDate: string;
  defaultEndDate: string;
}

const ExportSessionsModal: React.FC<ExportSessionsModalProps> = ({
  isOpen,
  onClose,
  sessions,
  defaultStartDate,
  defaultEndDate,
}) => {
  const { t } = useLanguage();
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [isExporting, setIsExporting] = useState(false);

  // Filter sessions by date range and include planned + active sessions
  const filteredSessions = useMemo(() => {
    return sessions.filter((session) => {
      const sessionDate = session.scheduled_date?.split('T')[0];
      if (!sessionDate) return false;
      // Include planned and active sessions
      const status = session.status;
      if (status !== 'pending' && status !== 'submitted' && status !== 'received' && status !== 'active' && status !== 'ready') return false;
      return sessionDate >= startDate && sessionDate <= endDate;
    });
  }, [sessions, startDate, endDate]);

  // Sort sessions by date, then by product SKU
  const sortedSessions = useMemo(() => {
    return [...filteredSessions].sort((a, b) => {
      const dateA = a.scheduled_date?.split('T')[0] || '';
      const dateB = b.scheduled_date?.split('T')[0] || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      return (a.product_sku || '').localeCompare(b.product_sku || '');
    });
  }, [filteredSessions]);

  const handleExport = async () => {
    if (sortedSessions.length === 0) return;

    setIsExporting(true);
    try {
      // Small delay to show loading state
      await new Promise((resolve) => setTimeout(resolve, 300));
      exportSessionsToExcel(sortedSessions, startDate, endDate);
      onClose();
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <FileSpreadsheet className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('supervisor.exportSessions')}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Date Range Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t('supervisor.selectDateRange')}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('supervisor.startDate')}
                  </label>
                  <DatePickerDropdown
                    selected={startDate || null}
                    onSelect={(date) => setStartDate(date)}
                  >
                    {({ onClick }) => (
                      <button type="button" onClick={onClick} className="w-full flex items-center justify-between pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        {startDate ? (() => { const d = new Date(startDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                      </button>
                    )}
                  </DatePickerDropdown>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('supervisor.endDate')}
                  </label>
                  <DatePickerDropdown
                    selected={endDate || null}
                    onSelect={(date) => setEndDate(date)}
                  >
                    {({ onClick }) => (
                      <button type="button" onClick={onClick} className="w-full flex items-center justify-between pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        {endDate ? (() => { const d = new Date(endDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                      </button>
                    )}
                  </DatePickerDropdown>
                </div>
              </div>
            </div>

            {/* Sessions Preview */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('supervisor.sessionsToExport')}
                </span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {sortedSessions.length}
                </span>
              </div>
              {sortedSessions.length === 0 && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                  {t('supervisor.noSessionsInRange')}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              disabled={isExporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || sortedSessions.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('supervisor.exporting')}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  {t('supervisor.exportToExcel')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportSessionsModal;
